import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { sessionCountry } from '../@core/lib/session'
import authConfig from 'src/configs/auth'
import { getSamplingRates } from './sampling'
import { normalizeDatadogSite, buildAllowedTracingUrls, createBeforeSendHandler } from './helpers'
import { COUNTRY_LABELS } from './constants'
import type { DatadogInitOptions, DatadogUser } from './types'

let initialized = false

export const initDatadog = (options?: DatadogInitOptions) => {
  if (initialized || typeof window === 'undefined') return

  const site = options?.site || process.env.NEXT_PUBLIC_DD_SITE || 'datadoghq.com'
  const applicationId = options?.applicationId || process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID || ''
  const clientToken = options?.clientToken || process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN || ''
  const service = options?.service || process.env.NEXT_PUBLIC_DD_SERVICE || 'resto365-frontend'
  const env = options?.env || process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.NODE_ENV || 'development'
  const version = options?.version || process.env.NEXT_PUBLIC_APP_VERSION || ''

  const allowedTracingUrls = buildAllowedTracingUrls(
    env,
    [
      process.env.NEXT_PUBLIC_R365_API_AU || '',
      process.env.NEXT_PUBLIC_R365_API_US || '',
      process.env.NEXT_PUBLIC_BHYVE_INTERNAL_API_AU || '',
      process.env.NEXT_PUBLIC_BHYVE_INTERNAL_API_US || '',
      process.env.NEXT_PUBLIC_BHYVE_EXTERNAL_API_AU || '',
      process.env.NEXT_PUBLIC_BHYVE_EXTERNAL_API_US || ''
    ],
    options
  )

  if (!clientToken || !applicationId) {
    return
  }

  const rates = getSamplingRates(env)
  const effectiveSampleRate = options?.sessionSampleRate ?? rates.session
  const effectiveReplayRate = options?.sessionReplaySampleRate ?? rates.replay
  const effectiveLogsRate = rates.logs
  const effectiveTraceRate = rates.trace

  const ddSite = normalizeDatadogSite(site)

  datadogRum.init({
    applicationId,
    clientToken,
    site: ddSite,
    service,
    env,
    version,
    sessionSampleRate: effectiveSampleRate,
    sessionReplaySampleRate: effectiveReplayRate,
    traceSampleRate: effectiveTraceRate,
    trackUserInteractions: options?.trackUserInteractions ?? true,
    trackResources: options?.trackResources ?? true,
    trackLongTasks: options?.trackLongTasks ?? true,
    defaultPrivacyLevel: options?.defaultPrivacyLevel ?? 'mask-user-input',
    allowedTracingUrls,
    trackViewsManually: false,
    useSecureSessionCookie: true,
    beforeSend: createBeforeSendHandler()
  })

  datadogRum.startSessionReplayRecording()

  datadogLogs.init({
    clientToken,
    site: ddSite,
    service,
    env,
    version,
    forwardErrorsToLogs: true,
    sessionSampleRate: effectiveLogsRate
  })

  // Add stable app-level country context independent of IP geolocation
  setUserCountryContext()

  initialized = true
}

const setUserCountryContext = () => {
  const countryKey = (sessionCountry || '').toUpperCase()
  const country = COUNTRY_LABELS[countryKey] || countryKey || 'UNKNOWN'

  datadogRum.setGlobalContext({ user_country: country })

  // Keep user_country in sync if country changes during the session
  try {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (!e.key || e.key !== authConfig.userCountryKeyName) return

      const k = (e.newValue || '').toUpperCase()
      const label = COUNTRY_LABELS[k] || k || 'UNKNOWN'
      datadogRum.setGlobalContext({ user_country: label })
    })
  } catch (_) {
    // Ignore errors in non-browser context or if localStorage is unavailable
  }
}

export const setDatadogUser = (user: DatadogUser | null) => {
  if (typeof window === 'undefined' || !initialized) return

  if (user) {
    datadogRum.setUser({
      id: String(user.id ?? user.email ?? ''),
      email: user.email,
      name: user.name
    })
  } else {
    datadogRum.clearUser()
  }
}

/**
 * Logs an error to Datadog RUM
 * Use this for handled errors that should still be captured by RUM
 * @param error - The error object or error message
 * @param context - Additional context to include with the error
 * @param source - The source of the error (e.g., 'redux', 'api', 'component')
 */
export const logErrorToRum = (error: Error | string | unknown, context?: Record<string, any>, source?: string) => {
  if (typeof window === 'undefined' || !initialized) return

  try {
    const errorMessage = error instanceof Error ? error.message : String(error)

    datadogRum.addError(error instanceof Error ? error : new Error(errorMessage), {
      source: source || 'application',
      ...context
    })
  } catch (_) {}
}
